import React from 'react';
import { prepareMusicHtml } from '../lib/musicEmbed';
import {
  addToSoundtrack, removeFromSoundtrack, isInSoundtrack, onSoundtrackChange,
} from '../lib/soundtrack';
import { InventoryTabBody } from './InventoryTab';

// ───────────── Shared shell + atoms ─────────────
const ModalShell = ({ open, onClose, width = 560, children, showCloseX }) => {
  if (!open) return null;
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(20, 40, 20, 0.55)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="room-themed-panel"
        style={{
          width,
          maxWidth: '94vw',
          maxHeight: '92vh',
          overflowY: 'auto',
          border: '2px solid',
          boxShadow: '0 8px 28px rgba(0,0,0,0.35)',
          padding: '22px 28px',
          fontFamily: 'Arial, Helvetica, sans-serif',
          position: 'relative',
        }}
      >
        {/* Msg #991 — Red X close button at the top-right of every modal. */}
        {showCloseX && (
          <button
            data-testid="modal-close-x"
            onClick={onClose}
            title="Close"
            aria-label="Close"
            style={{
              position: 'absolute',
              top: 6, right: 8,
              width: 26, height: 26,
              background: '#c0392b',
              color: '#ffffff',
              border: '1px solid #7a1f14',
              borderRadius: 4,
              fontWeight: 'bold',
              fontSize: 14,
              lineHeight: '20px',
              cursor: 'pointer',
              zIndex: 2,
            }}
          >✕</button>
        )}
        {children}
      </div>
    </div>
  );
};

const FieldInput = (props) => (
  <input
    {...props}
    style={{
      backgroundColor: '#1c3a1c',
      color: '#d4f1c4',
      border: '1px solid #2d6b1e',
      padding: '3px 6px',
      fontFamily: 'Arial, sans-serif',
      fontSize: '13px',
      height: '22px',
      width: '280px',
      ...(props.style || {}),
    }}
  />
);

const FieldTextarea = (props) => (
  <textarea
    {...props}
    style={{
      backgroundColor: '#1c3a1c',
      color: '#d4f1c4',
      border: '1px solid #2d6b1e',
      padding: '4px 6px',
      fontFamily: 'Arial, sans-serif',
      fontSize: '13px',
      width: '280px',
      resize: 'vertical',
      ...(props.style || {}),
    }}
  />
);

const FieldSelect = ({ value, onChange, children, style }) => (
  <select
    value={value}
    onChange={onChange}
    style={{
      backgroundColor: '#1c3a1c',
      color: '#d4f1c4',
      border: '1px solid #2d6b1e',
      padding: '2px 4px',
      fontFamily: 'Arial, sans-serif',
      fontSize: '13px',
      height: '24px',
      width: '292px',
      ...(style || {}),
    }}
  >
    {children}
  </select>
);

const ActionBtn = ({ children, onClick, type = 'button', style, ...rest }) => (
  <button
    type={type}
    onClick={onClick}
    {...rest}
    style={{
      backgroundColor: '#1c3a1c',
      color: '#ecfcd9',
      border: '1px solid #2d6b1e',
      padding: '4px 14px',
      fontWeight: 'bold',
      fontSize: '13px',
      cursor: 'pointer',
      fontFamily: 'Arial, sans-serif',
      margin: '0 4px',
      ...(style || {}),
    }}
  >
    {children}
  </button>
);

const Required = () => <span style={{ color: '#c0392b', marginLeft: 6 }}>*</span>;

// Msg #799/#800 — Memoized so parent polling re-renders (every ~2 s) do NOT
// re-mount the iframe (which was restarting the track from scratch on every
// tick — the user-reported "auto-refresh" glitch).  The `prepareMusicHtml`
// helper (in `../lib/musicEmbed`) rewrites iframe URLs for autoplay and
// injects a well-formed `allow="autoplay…"` attribute.
const MusicEmbed = React.memo(function MusicEmbed({ musicHtml }) {
  const prepared = React.useMemo(() => prepareMusicHtml(musicHtml), [musicHtml]);
  if (!prepared) return null;
  return (
    <div
      data-testid="profile-music-player"
      style={{
        marginTop: 10,
        background: '#1a1a1a',
        border: '1px solid #5a4a1d',
        padding: 6,
        display: 'flex', justifyContent: 'center',
      }}
      dangerouslySetInnerHTML={{ __html: prepared }}
    />
  );
}, (prev, next) => prev.musicHtml === next.musicHtml);

// Msg #800 enhancement — Save/Remove-from-Adventure-Soundtrack pill placed
// under the music player. Bookmarks the NPC's track into a personal playlist
// (localStorage) so the floating player on ChatPage can auto-mount it
// whenever the user enters a room where this NPC is present.
const SoundtrackSaveButton = ({ npc, musicHtml }) => {
  const [saved, setSaved] = React.useState(() => isInSoundtrack(npc));
  React.useEffect(() => onSoundtrackChange(() => setSaved(isInSoundtrack(npc))), [npc]);
  const toggle = React.useCallback(() => {
    if (saved) {
      removeFromSoundtrack(npc);
    } else {
      // Msg #800 — Save the AUTOPLAY-PREPARED HTML (not the raw admin input)
      // so the floating player can drop it into the DOM and immediately start
      // playing. Otherwise the floating player would render the raw iframe
      // without any autoplay params and never auto-start.
      addToSoundtrack(npc, prepareMusicHtml(musicHtml));
    }
  }, [saved, npc, musicHtml]);
  return (
    <div style={{ textAlign: 'center', marginTop: 6 }}>
      <button
        data-testid={saved ? `soundtrack-unsave-${npc}` : `soundtrack-save-${npc}`}
        onClick={toggle}
        title={saved
          ? 'Remove this NPC from your Adventure Soundtrack'
          : 'Save to Adventure Soundtrack — plays when this NPC is in your room'}
        style={{
          background: saved ? '#5a4a1d' : '#1c3a1c',
          color: '#f5e6c0',
          border: '1px solid #5a4a1d',
          padding: '4px 12px',
          fontFamily: 'Georgia, serif',
          fontSize: 11,
          letterSpacing: 1,
          textTransform: 'uppercase',
          cursor: 'pointer',
        }}
      >
        {saved ? '♬ ✓ In Soundtrack' : '♬ Save to Playlist'}
      </button>
    </div>
  );
};

// Msg #798 — Convert a raw YouTube / Vimeo watch-URL into an embeddable
// iframe URL so it plays inline on the character profile.  Returns null for
// non-YouTube/Vimeo URLs (caller falls back to <video> or <img>).
const _toEmbeddableVideoUrl = (url) => {
  if (!url) return null;
  const u = String(url).trim();
  // YouTube — watch?v=, youtu.be/, shorts/, embed/
  let m = u.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|shorts\/|embed\/))([\w-]{6,})/i);
  if (m) return `https://www.youtube.com/embed/${m[1]}?autoplay=0&rel=0`;
  // Vimeo — vimeo.com/123 or player.vimeo.com/video/123
  m = u.match(/vimeo\.com\/(?:video\/)?(\d+)/i);
  if (m) return `https://player.vimeo.com/video/${m[1]}`;
  return null;
};

// ───────────── Helpers ─────────────
const PROFILES_KEY = 'mandala_profiles';

export const loadProfiles = () => {
  try {
    return JSON.parse(localStorage.getItem(PROFILES_KEY) || '{}');
  } catch {
    return {};
  }
};

const saveProfiles = (p) => localStorage.setItem(PROFILES_KEY, JSON.stringify(p));

export const getProfile = (username) => loadProfiles()[username] || null;

export const upsertProfile = (profile) => {
  const all = loadProfiles();
  all[profile.username] = profile;
  saveProfiles(all);
};

const removeProfile = (username) => {
  const all = loadProfiles();
  delete all[username];
  saveProfiles(all);
};

// Safe HTML rendering — only allow a small whitelist
const ALLOWED_TAGS = ['b', 'i', 'u', 'hr', 'small', 'center', 'right', 'br', 'em', 'strong'];
export const sanitizeProfileHTML = (raw) => {
  if (!raw) return '';
  let s = String(raw);
  // Strip <script>, <style>, <iframe> blocks
  s = s.replace(/<\s*(script|style|iframe)[\s\S]*?<\s*\/\s*\1\s*>/gi, '');
  // Remove inline event handlers and javascript: urls
  s = s.replace(/\son\w+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, '');
  s = s.replace(/javascript:/gi, '');
  // Strip any tag not in allow list (keeps inner text)
  s = s.replace(/<\/?([a-zA-Z][a-zA-Z0-9]*)\b[^>]*>/g, (m, tag) => {
    return ALLOWED_TAGS.includes(tag.toLowerCase()) ? m : '';
  });
  // Convert <right> and <center> to inline divs so they render in browsers
  s = s.replace(/<\s*center\s*>/gi, '<div style="text-align:center">');
  s = s.replace(/<\/\s*center\s*>/gi, '</div>');
  s = s.replace(/<\s*right\s*>/gi, '<div style="text-align:right">');
  s = s.replace(/<\/\s*right\s*>/gi, '</div>');
  return s;
};

// ───────────── Re-usable profile form (Register + Edit) ─────────────
const ProfileFormBody = ({ form, setForm, lockUsername = false }) => {
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <>
      <table style={{ margin: '14px auto 0', borderCollapse: 'separate', borderSpacing: '0 6px' }}>
        <tbody>
          <tr>
            <td style={{ textAlign: 'right' }}>Your Username :</td>
            <td>
              <FieldInput
                value={form.username}
                onChange={(e) => update('username', e.target.value)}
                disabled={lockUsername}
              />
              <Required />
            </td>
          </tr>
          <tr>
            <td style={{ textAlign: 'right' }}>Your Password :</td>
            <td>
              <FieldInput
                type="password"
                value={form.password}
                onChange={(e) => update('password', e.target.value)}
              />
              <Required />
            </td>
          </tr>
          <tr>
            <td style={{ textAlign: 'right' }}>Firstname :</td>
            <td><FieldInput value={form.firstname} onChange={(e) => update('firstname', e.target.value)} /></td>
          </tr>
          <tr>
            <td style={{ textAlign: 'right' }}>Lastname :</td>
            <td><FieldInput value={form.lastname} onChange={(e) => update('lastname', e.target.value)} /></td>
          </tr>
          <tr>
            <td style={{ textAlign: 'right' }}>Gender :</td>
            <td>
              <FieldSelect value={form.gender} onChange={(e) => update('gender', e.target.value)}>
                <option value="">— select —</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="nonbinary">Non-binary</option>
                <option value="other">Other</option>
                <option value="undisclosed">Prefer not to say</option>
              </FieldSelect>
            </td>
          </tr>
          <tr>
            <td style={{ textAlign: 'right' }}>Website URL :</td>
            <td><FieldInput value={form.website} onChange={(e) => update('website', e.target.value)} /></td>
          </tr>
          <tr>
            <td style={{ textAlign: 'right' }}>Your E-mail :</td>
            <td>
              <FieldInput value={form.email} onChange={(e) => update('email', e.target.value)} />
              <Required />
            </td>
          </tr>
          <tr>
            <td></td>
            <td>
              <label>
                <input
                  type="checkbox"
                  checked={!!form.showEmail}
                  onChange={(e) => update('showEmail', e.target.checked)}
                />{' '}
                Show e-mail in public information
              </label>
            </td>
          </tr>
          <tr>
            <td style={{ textAlign: 'right', paddingTop: 4 }}>Profile Type :</td>
            <td>
              <label style={{ marginRight: 12 }}>
                <input
                  type="radio"
                  data-testid="profile-ic-radio"
                  name="ic_ooc"
                  checked={(form.ic_ooc || 'IC') === 'IC'}
                  onChange={() => update('ic_ooc', 'IC')}
                />{' '}
                IC (In Character)
              </label>
              <label>
                <input
                  type="radio"
                  data-testid="profile-ooc-radio"
                  name="ic_ooc"
                  checked={form.ic_ooc === 'OOC'}
                  onChange={() => update('ic_ooc', 'OOC')}
                />{' '}
                OOC (Out of Character)
              </label>
            </td>
          </tr>
        </tbody>
      </table>

      <div style={{ textAlign: 'center', marginTop: 10, fontWeight: 'bold' }}>
        Your Character Description :
      </div>
      <div style={{ textAlign: 'center' }}>
        <FieldTextarea
          rows={8}
          value={form.description}
          onChange={(e) => update('description', e.target.value)}
          style={{ width: '85%', minHeight: 140 }}
          placeholder="HTML allowed: <b>, <i>, <u>, <hr>, <small>, <center>, <right>, <br>"
        />
        <div style={{ fontStyle: 'italic', fontSize: 12, marginTop: 4 }}>
          Valid HTML tags: &lt;b&gt;, &lt;u&gt;, &lt;i&gt;, &lt;hr&gt;, &lt;small&gt;, &lt;center&gt;, &lt;right&gt;, &lt;br&gt;
        </div>
      </div>

      <table style={{ margin: '12px auto 0' }}>
        <tbody>
          <tr>
            <td style={{ textAlign: 'right', verticalAlign: 'top', paddingTop: 4 }}>
              Picture URLs :<br />
              <span style={{ fontSize: 10, color: '#4a5a3a', fontWeight: 'normal' }}>(up to 20)</span>
            </td>
            <td>
              {Array.from({ length: 20 }).map((_, i) => {
                const arr = Array.isArray(form.pictures) ? form.pictures : [];
                const val = arr[i] !== undefined ? arr[i] : (i === 0 ? (form.picture || '') : '');
                return (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                    <FieldInput
                      data-testid={`profile-picture-url-${i}`}
                      value={val && val.startsWith('data:') ? '' : val}
                      onChange={(e) => {
                        // Msg #786 — Keep the raw slot value intact while
                        // typing. We used to trim + filter(Boolean) on every
                        // keystroke, which collapsed empty slots and blanked
                        // the input mid-typing. Save-time is where cleanup
                        // happens (see save() below).
                        const next = Array.from({ length: 20 }, (_, j) => {
                          if (Array.isArray(form.pictures) && form.pictures[j] !== undefined) {
                            return form.pictures[j];
                          }
                          return j === 0 ? (form.picture || '') : '';
                        });
                        next[i] = e.target.value;
                        update('pictures', next);
                        if (i === 0) update('picture', e.target.value);  // first slot doubles as main avatar
                      }}
                      placeholder={i === 0 ? 'https://... (main avatar)' : `https://... (extra ${i})`}
                    />
                    {val && (
                      <button
                        type="button"
                        data-testid={`profile-picture-clear-${i}`}
                        onClick={() => {
                          const next = Array.from({ length: 20 }, (_, j) => {
                            if (Array.isArray(form.pictures) && form.pictures[j] !== undefined) {
                              return form.pictures[j];
                            }
                            return j === 0 ? (form.picture || '') : '';
                          });
                          next[i] = '';
                          update('pictures', next);
                          if (i === 0) update('picture', '');
                        }}
                        style={{
                          backgroundColor: '#5d2a2a', color: '#fce2e2',
                          border: '1px solid #3a1a1a', fontSize: 10,
                          padding: '2px 6px', cursor: 'pointer',
                        }}
                      >×</button>
                    )}
                  </div>
                );
              })}
            </td>
          </tr>
        </tbody>
      </table>

      {(() => {
        const arr = Array.isArray(form.pictures) && form.pictures.length ? form.pictures : (form.picture ? [form.picture] : []);
        if (!arr.length) return null;
        return (
          <div style={{ textAlign: 'center', marginTop: 10, display: 'flex', gap: 6, justifyContent: 'center', flexWrap: 'wrap' }}>
            {arr.slice(0, 6).map((src, i) => (
              <img
                key={i}
                src={src}
                alt={`preview ${i + 1}`}
                style={{ maxWidth: 84, maxHeight: 84, border: '1px solid #2d6b1e' }}
                onError={(e) => (e.target.style.display = 'none')}
              />
            ))}
          </div>
        );
      })()}
    </>
  );
};

// ───────────── Register Modal ─────────────
export const RegisterModal = ({ open, onClose }) => {
  const blank = () => ({
    username: '',
    password: '',
    firstname: '',
    lastname: '',
    gender: '',
    website: '',
    email: '',
    showEmail: false,
    description: '',
    picture: '',
  });
  const [form, setForm] = React.useState(blank());
  const [msg, setMsg] = React.useState('');

  React.useEffect(() => {
    if (open) {
      setForm(blank());
      setMsg('');
    }
  }, [open]);

  const submit = async () => {
    if (!form.username.trim() || !form.password.trim() || !form.email.trim()) {
      setMsg('Please fill in all fields marked with *.');
      return;
    }
    try {
      // Msg #802 — Submit to the admin-approval workflow. Profile data still
      // saved locally for later profile-editing convenience.
      const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
      const res = await fetch(`${API}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: form.username.trim(),
          email: form.email.trim(),
          password: form.password,
          reason: form.description || '',
        }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) {
        setMsg(d.detail || 'Registration failed.');
        return;
      }
      upsertProfile({ ...form, createdAt: new Date().toISOString() });
      setMsg(d.message || 'Registration submitted — awaiting admin approval.');
      setTimeout(() => { setMsg(''); onClose(); }, 2000);
    } catch (e) {
      setMsg(`Registration failed: ${e.message || e}`);
    }
  };

  return (
    <ModalShell open={open} onClose={onClose} width={640}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontWeight: 'bold', fontSize: 18 }}>User Registration</div>
        <div style={{ fontWeight: 'bold', fontSize: 14, marginTop: 4 }}>
          Fields with a <span style={{ color: '#c0392b' }}>*</span> must be completed.
        </div>
      </div>

      <ProfileFormBody form={form} setForm={setForm} />

      {msg && <div style={{ textAlign: 'center', marginTop: 10, color: '#1a4a10', fontWeight: 'bold' }}>{msg}</div>}

      <div style={{ textAlign: 'center', marginTop: 14 }}>
        <ActionBtn onClick={submit}>Register</ActionBtn>
        <ActionBtn onClick={onClose}>Close</ActionBtn>
      </div>
    </ModalShell>
  );
};

// ───────────── Edit Profile Modal (login-gated) ─────────────
export const EditProfileModal = ({ open, onClose, userId, apiBase }) => {
  const [step, setStep] = React.useState('login'); // 'login' | 'edit'
  const [loginUser, setLoginUser] = React.useState('');
  const [loginPwd, setLoginPwd] = React.useState('');
  const [loginErr, setLoginErr] = React.useState('');
  const [form, setForm] = React.useState(null);
  const [msg, setMsg] = React.useState('');
  const [blockedList, setBlockedList] = React.useState([]);

  const refreshBlocks = React.useCallback(async () => {
    if (!userId || !apiBase) return;
    try {
      const r = await fetch(`${apiBase}/blocks?user_id=${encodeURIComponent(userId)}`);
      const d = await r.json().catch(() => ({}));
      setBlockedList(d.blocked || []);
    } catch { /* ignore */ }
  }, [userId, apiBase]);

  const unblock = async (name) => {
    if (!userId || !apiBase) return;
    try {
      await fetch(`${apiBase}/blocks/remove`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, blocked_name: name }),
      });
      setBlockedList((prev) => prev.filter((n) => n !== name));
    } catch { /* ignore */ }
  };

  React.useEffect(() => {
    if (open) {
      setStep('login');
      setLoginUser(sessionStorage.getItem('keep_username') || '');
      setLoginPwd('');
      setLoginErr('');
      setForm(null);
      setMsg('');
    }
  }, [open]);

  // Refresh the block list whenever the user actually opens the Edit step.
  React.useEffect(() => {
    if (open && step === 'edit') refreshBlocks();
  }, [open, step, refreshBlocks]);

  const tryLogin = () => {
    const p = getProfile(loginUser.trim());
    if (!p) {
      // Master password also allows admin to create/edit any (even nonexistent) profile
      if (loginPwd === 'kittymew') {
        setForm({
          username: loginUser.trim(), password: '', firstname: '', lastname: '',
          gender: '', website: '', email: '', showEmail: false, description: '', picture: '',
        });
        setLoginErr('');
        setStep('edit');
        return;
      }
      setLoginErr('No profile found for that username. Please Register first.');
      return;
    }
    if (p.password !== loginPwd && loginPwd !== 'kittymew') {
      setLoginErr('Incorrect password.');
      return;
    }
    setForm({ ...p });
    setLoginErr('');
    setStep('edit');
  };

  const save = async () => {
    if (!form.username.trim() || !form.password.trim() || !form.email.trim()) {
      setMsg('Please fill in all fields marked with *.');
      return;
    }
    const payload = { ...form, updatedAt: new Date().toISOString() };
    upsertProfile(payload);
    // Also persist to backend so other users (and the global search) can find this profile
    // even when the owner is logged off.
    if (apiBase) {
      try {
        await fetch(`${apiBase}/profiles/save`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            username: form.username.trim(),
            firstname: form.firstname || '',
            lastname:  form.lastname  || '',
            gender:    form.gender    || '',
            description: form.description || '',
            picture:   form.picture   || '',
            website:   form.website   || '',
            email:     form.email     || '',
            showEmail: !!form.showEmail,
            ic_ooc:    form.ic_ooc    || 'IC',
            pictures:  Array.isArray(form.pictures) ? form.pictures.filter(Boolean).slice(0, 20) : (form.picture ? [form.picture] : []),
          }),
        });
      } catch { /* offline → localStorage still saved */ }
    }
    setMsg('Profile saved.');
    setTimeout(() => {
      setMsg('');
      onClose();
    }, 900);
  };

  return (
    <ModalShell open={open} onClose={onClose} width={step === 'login' ? 420 : 720}>
      {step === 'login' && (
        <>
          <div style={{ textAlign: 'center', fontWeight: 'bold', fontSize: 22 }}>Login</div>
          <div style={{ textAlign: 'center', fontSize: 12, marginTop: 4, color: '#1a4a10' }}>
            Confirm your credentials to edit your profile.
          </div>
          <table style={{ margin: '18px auto 0', borderCollapse: 'separate', borderSpacing: '0 8px' }}>
            <tbody>
              <tr>
                <td style={{ textAlign: 'right' }}>Your Username :</td>
                <td>
                  <FieldInput
                    autoFocus
                    value={loginUser}
                    onChange={(e) => setLoginUser(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && tryLogin()}
                  />
                </td>
              </tr>
              <tr>
                <td style={{ textAlign: 'right' }}>Your Password :</td>
                <td>
                  <FieldInput
                    type="password"
                    value={loginPwd}
                    onChange={(e) => setLoginPwd(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && tryLogin()}
                  />
                </td>
              </tr>
            </tbody>
          </table>
          {loginErr && (
            <div style={{ textAlign: 'center', marginTop: 8, color: '#a02020', fontWeight: 'bold' }}>{loginErr}</div>
          )}
          <div style={{ textAlign: 'center', marginTop: 14 }}>
            <ActionBtn onClick={tryLogin}>Log In</ActionBtn>
            <ActionBtn onClick={onClose}>Close</ActionBtn>
          </div>
        </>
      )}

      {step === 'edit' && form && (
        <>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontWeight: 'bold', fontSize: 18 }}>Editing user profile</div>
            <div style={{ fontWeight: 'bold', fontSize: 14, marginTop: 4 }}>
              Fields with a <span style={{ color: '#c0392b' }}>*</span> must be completed.
            </div>
          </div>
          <ProfileFormBody form={form} setForm={setForm} lockUsername />
          {/* ───── Ignored Users panel ───── */}
          <div
            data-testid="ignored-users-panel"
            style={{
              marginTop: 14,
              border: '1px solid #2d6b1e',
              background: '#e9f3da',
              padding: '8px 10px',
            }}
          >
            <div style={{ fontWeight: 'bold', fontSize: 13, color: '#1a4a10' }}>
              Ignored Users {blockedList.length > 0 && <span style={{ fontWeight: 'normal' }}>({blockedList.length})</span>}
            </div>
            {blockedList.length === 0 ? (
              <div style={{ fontSize: 12, color: '#3a5a3a', fontStyle: 'italic', marginTop: 4 }}>
                You aren&apos;t ignoring anyone right now.
              </div>
            ) : (
              <ul style={{ listStyle: 'none', padding: 0, margin: '6px 0 0 0' }}>
                {blockedList.map((name) => (
                  <li
                    key={name}
                    data-testid={`ignored-row-${name}`}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      padding: '3px 0', fontSize: 12, borderBottom: '1px dotted #b6cf9c',
                    }}
                  >
                    <span style={{ flex: 1, color: '#1c3f1c' }}>{name}</span>
                    <button
                      data-testid={`unblock-${name}`}
                      onClick={() => unblock(name)}
                      style={{
                        background: '#1c3a1c', color: '#ecfcd9',
                        border: '1px solid #2d6b1e',
                        padding: '2px 10px', fontSize: 11, fontWeight: 'bold',
                        cursor: 'pointer',
                      }}
                    >
                      Unblock
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          {msg && (
            <div style={{ textAlign: 'center', marginTop: 10, color: '#1a4a10', fontWeight: 'bold' }}>{msg}</div>
          )}
          <div style={{ textAlign: 'center', marginTop: 14 }}>
            <ActionBtn onClick={save}>Change</ActionBtn>
            <ActionBtn onClick={onClose}>Close</ActionBtn>
          </div>
        </>
      )}
    </ModalShell>
  );
};

// ───────────── Delete User Modal (login → confirm) ─────────────
export const DeleteUserModal = ({ open, onClose }) => {
  const [step, setStep] = React.useState('login'); // 'login' | 'confirm' | 'done'
  const [username, setUsername] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [err, setErr] = React.useState('');

  React.useEffect(() => {
    if (open) {
      setStep('login');
      setUsername('');
      setPassword('');
      setErr('');
    }
  }, [open]);

  const verify = () => {
    const p = getProfile(username.trim());
    if (!p) {
      // Master password admin path — still requires the user to exist for deletion
      setErr('No such user.');
      return;
    }
    if (p.password !== password && password !== 'kittymew') {
      setErr('Incorrect password.');
      return;
    }
    setErr('');
    setStep('confirm');
  };

  const confirmYes = () => {
    removeProfile(username.trim());
    setStep('done');
    setTimeout(() => {
      onClose();
    }, 900);
  };

  return (
    <ModalShell open={open} onClose={onClose} width={420}>
      {step === 'login' && (
        <>
          <div style={{ textAlign: 'center', fontWeight: 'bold', fontSize: 22 }}>Login</div>
          <div style={{ textAlign: 'center', fontSize: 12, marginTop: 4, color: '#1a4a10' }}>
            Confirm your credentials to delete the user.
          </div>
          <table style={{ margin: '18px auto 0', borderCollapse: 'separate', borderSpacing: '0 8px' }}>
            <tbody>
              <tr>
                <td style={{ textAlign: 'right' }}>Your Username :</td>
                <td>
                  <FieldInput
                    autoFocus
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && verify()}
                  />
                </td>
              </tr>
              <tr>
                <td style={{ textAlign: 'right' }}>Your Password :</td>
                <td>
                  <FieldInput
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && verify()}
                  />
                </td>
              </tr>
            </tbody>
          </table>
          {err && <div style={{ textAlign: 'center', marginTop: 8, color: '#a02020', fontWeight: 'bold' }}>{err}</div>}
          <div style={{ textAlign: 'center', marginTop: 14 }}>
            <ActionBtn onClick={verify}>Log In</ActionBtn>
            <ActionBtn onClick={onClose}>Close</ActionBtn>
          </div>
        </>
      )}

      {step === 'confirm' && (
        <>
          <div style={{ textAlign: 'center', fontWeight: 'bold', fontSize: 22 }}>Deleting user</div>
          <div style={{ textAlign: 'center', fontSize: 16, marginTop: 18 }}>
            Do you really want to be removed ?
          </div>
          <div style={{ textAlign: 'center', marginTop: 26 }}>
            <ActionBtn onClick={confirmYes} style={{ minWidth: 60 }}>Yes</ActionBtn>
            <ActionBtn onClick={onClose} style={{ minWidth: 60 }}>No</ActionBtn>
          </div>
        </>
      )}

      {step === 'done' && (
        <div style={{ textAlign: 'center', fontWeight: 'bold', fontSize: 16, padding: '20px 0' }}>
          User &laquo;{username}&raquo; has been deleted.
        </div>
      )}
    </ModalShell>
  );
};

// ───────────── View Profile Modal ─────────────
export const ViewProfileModal = ({ open, onClose, character, isAdmin, onEditNpc, onDeleteNpc, userId, apiBase, onContinueScene, onContinueRp, onPm, onSendFriendRequest, myName, cloaked, onToggleCloak, puppetNpc, onTogglePuppet, onOpenEditProfile, onDeleteUser, requesterName }) => {
  const [tab, setTab] = React.useState('profile'); // 'profile' | 'history' | 'past_rps' | 'stats'
  const [history, setHistory] = React.useState([]);
  const [historyLoading, setHistoryLoading] = React.useState(false);
  const [historyErr, setHistoryErr] = React.useState('');
  const [favorites, setFavorites] = React.useState([]);
  const [pastRps, setPastRps] = React.useState([]);
  const [pastRpsLoading, setPastRpsLoading] = React.useState(false);
  const [stats, setStats] = React.useState(null);
  // Server-persisted profile (so any user can view another user's profile + updated_at)
  const [serverProfile, setServerProfile] = React.useState(null);
  // Adventurer rank derived from cumulative combat damage (fetched per open)
  const [rankInfo, setRankInfo] = React.useState(null);
  // Msg #798 — Interactive gallery: which image is currently displayed.
  const [galleryIdx, setGalleryIdx] = React.useState(0);
  React.useEffect(() => { setGalleryIdx(0); }, [character && character.name, open]);

  // Fetch adventurer rank when the modal opens for a real (non-NPC) user
  React.useEffect(() => {
    if (!open || !character || character.isNPC || !apiBase || !character.name) {
      setRankInfo(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${apiBase}/combat/rank?username=${encodeURIComponent(character.name)}`);
        if (!r.ok) return;
        const d = await r.json();
        if (!cancelled) setRankInfo(d);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, character?.name, apiBase]);

  // Reset tab whenever the modal opens for a new character
  React.useEffect(() => {
    if (open) setTab('profile');
  }, [open, character && character.name]);

  // Fetch the server-side profile (for real users — NPCs aren't in user_profiles)
  React.useEffect(() => {
    if (!open || !character || character.isNPC || !apiBase) {
      setServerProfile(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${apiBase}/profiles/get?username=${encodeURIComponent(character.name)}`);
        if (!r.ok) return;
        const d = await r.json();
        if (!cancelled) setServerProfile(d.profile || null);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [open, character?.name, apiBase]);

  // Load conversation history + favorites when the user switches to History tab
  // Msg #797 — depend on `character?.name` (stable identity) instead of the
  // whole `character` prop, otherwise every parent re-render (via
  // applyOverride returning a new object) re-fires the effect and glitches the
  // panel (loading flash + scroll reset).
  React.useEffect(() => {
    if (!open || tab !== 'history' || !character || !character.isNPC || !userId || !apiBase) return;
    let cancelled = false;
    const load = async () => {
      setHistoryLoading(true); setHistoryErr('');
      try {
        const [hRes, fRes] = await Promise.all([
          fetch(`${apiBase}/npc/memory?user_id=${encodeURIComponent(userId)}&npc=${encodeURIComponent(character.name)}&limit=12`),
          fetch(`${apiBase}/npc/favorites?user_id=${encodeURIComponent(userId)}&npc=${encodeURIComponent(character.name)}`),
        ]);
        if (!hRes.ok) throw new Error(`HTTP ${hRes.status}`);
        const hd = await hRes.json();
        const fd = fRes.ok ? await fRes.json() : { items: [] };
        if (!cancelled) {
          setHistory(hd.turns || []);
          setFavorites(fd.items || []);
        }
      } catch (e) {
        if (!cancelled) setHistoryErr(String(e.message || e));
      } finally {
        if (!cancelled) setHistoryLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tab, character?.name, userId, apiBase]);

  // Load Past RPs when switching to that tab
  React.useEffect(() => {
    if (!open || tab !== 'past_rps' || !character || !character.isNPC || !userId || !apiBase) return;
    let cancelled = false;
    (async () => {
      setPastRpsLoading(true);
      try {
        const r = await fetch(`${apiBase}/npc/past_rps/list?user_id=${encodeURIComponent(userId)}&npc=${encodeURIComponent(character.name)}&limit=30`);
        const d = r.ok ? await r.json() : { items: [] };
        if (!cancelled) setPastRps(d.items || []);
      } catch { /* ignore */ }
      finally { if (!cancelled) setPastRpsLoading(false); }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tab, character?.name, userId, apiBase]);

  // Load Character Stats when switching to that tab
  React.useEffect(() => {
    if (!open || tab !== 'stats' || !character || !character.isNPC || !userId || !apiBase) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${apiBase}/npc/stats?user_id=${encodeURIComponent(userId)}&npc=${encodeURIComponent(character.name)}`);
        const d = r.ok ? await r.json() : null;
        if (!cancelled) setStats(d);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tab, character?.name, userId, apiBase]);

  // Msg #774 — Look up whether this user has unlocked the feminized form of
  // an NPC (Werewolf via magic, Gel via tickling, Overlord_Mortis at affection 15).
  const [feminized, setFeminized] = React.useState(false);
  React.useEffect(() => {
    if (!open || !character || !character.isNPC || !userId || !apiBase) {
      setFeminized(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${apiBase}/npc/transformations?user_id=${encodeURIComponent(userId)}&npc=${encodeURIComponent(character.name)}`);
        const d = r.ok ? await r.json() : { feminized: false };
        if (!cancelled) setFeminized(!!d.feminized);
      } catch { if (!cancelled) setFeminized(false); }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, character?.name, character?.isNPC, userId, apiBase]);

  const addFavorite = async (text) => {
    if (!userId || !apiBase || !character || !text) return;
    try {
      const r = await fetch(`${apiBase}/npc/favorites/add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, npc: character.name, text, source: 'npc_reply' }),
      });
      const d = await r.json();
      if (d.item) setFavorites((prev) => [...prev, d.item]);
    } catch { /* ignore */ }
  };

  const removeFavorite = async (favId) => {
    if (!userId || !apiBase || !character) return;
    try {
      await fetch(`${apiBase}/npc/favorites/remove`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, npc: character.name, fav_id: favId }),
      });
      setFavorites((prev) => prev.filter((f) => f.id !== favId));
    } catch { /* ignore */ }
  };

  // Session AD (Msg #972) — live HP + rank stats hooks MUST be declared before
  // the `if (!character) return null` early return to comply with rules-of-hooks.
  const [userLiveStats, setUserLiveStats] = React.useState(null);
  React.useEffect(() => {
    const isRealUser = !!(open && character && !character.isNPC && apiBase && character.name);
    if (!isRealUser || tab !== 'stats') return;
    let cancelled = false;
    (async () => {
      try {
        const [rankResp, hpResp] = await Promise.all([
          fetch(`${apiBase}/combat/user_stats?username=${encodeURIComponent(character.name)}`).then((r) => r.ok ? r.json() : null),
          fetch(`${apiBase}/user/hp?username=${encodeURIComponent(character.name)}`).then((r) => r.ok ? r.json() : null),
        ]);
        if (!cancelled) setUserLiveStats({ rank: rankResp, hp: hpResp });
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [open, tab, apiBase, character && character.isNPC, character && character.name]);

  if (!character) return null;
  // For non-NPCs we prefer the SERVER-side profile (so user B can see user A's saved
  // description/picture even though it isn't in B's localStorage). We still fall back
  // to local saved profile (this browser's own data) and finally to the character props.
  const savedProfile = getProfile(character.name);
  const baseProfile = (!character.isNPC && serverProfile) ? serverProfile : savedProfile;
  const data = baseProfile || {
    username: character.name,
    firstname: character.firstname || '',
    lastname: character.lastname || '',
    gender: character.gender || '',
    description: character.description || `${character.name} wanders the realms of Mandala.`,
    picture: character.picture || '',
    // Msg #797 — carry NPC gallery + feminine variant through the fallback path
    // so the ViewProfileModal renders them the moment the admin saves.
    pictures: Array.isArray(character.pictures) ? character.pictures : [],
    feminine_picture: character.feminine_picture || '',
    // Msg #797 batch — NPC profiles can now expose an external website and an
    // embedded profile video (mp4/webm/gif) via /admin/edit_npc.
    website: character.website || '',
    video_url: character.video_url || '',
    // Msg #798 batch — dedicated animated GIF slot + music player HTML embed
    gif_url: character.gif_url || '',
    music_html: character.music_html || '',
    email: character.email || '',
    showEmail: false,
  };

  const showHistoryTab = !!(character.isNPC && userId && apiBase);
  // Msg #778 — own-profile Edit tab is a separate signal from the NPC history tabs.
  const showOwnEditTab = !!(!character.isNPC && myName && character.name === myName);
  // Session AD (Msg #972) — real users also get a CHARACTER STATS tab that
  // shows their live HP + rank-derived Attack / HP-max / Defense.
  const showUserStatsTab = !!(!character.isNPC && apiBase && character.name);
  // Msg #996 — Inventory tab. Only visible when viewing YOUR OWN profile.
  const showInventoryTab = !!(!character.isNPC && myName && character.name === myName && apiBase);
  const showTabStrip = showHistoryTab || showOwnEditTab || showUserStatsTab || showInventoryTab;

  const tabBtnStyle = (active) => ({
    background: active ? '#1c3a1c' : '#cce0b0',
    color: active ? '#ecfcd9' : '#1a4a10',
    border: '1px solid #2d6b1e',
    borderBottom: active ? 'none' : '1px solid #2d6b1e',
    padding: '4px 14px',
    fontSize: 12,
    fontWeight: 'bold',
    cursor: 'pointer',
    letterSpacing: 1,
    marginRight: 2,
  });

  const formatStamp = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString();
  };

  return (
    <ModalShell open={open} onClose={onClose} width={560} showCloseX>
      <div style={{ textAlign: 'center', fontWeight: 'bold', fontSize: 20 }}>Character Profile</div>
      <div
        style={{
          textAlign: 'center',
          fontWeight: 'bold',
          fontSize: 18,
          marginTop: 4,
          color: character.color || '#1c3f1c',
        }}
      >
        <span
          data-testid="profile-online-dot"
          title={
            character.isNPC ? 'NPC — always available'
              : (serverProfile && serverProfile.online ? 'Online' : 'Offline / in private room')
          }
          style={{
            display: 'inline-block', width: 10, height: 10, borderRadius: '50%',
            border: '1px solid #1c3a1c',
            background: (character.isNPC || (serverProfile && serverProfile.online)) ? '#1f9d3a' : '#a02020',
            marginRight: 6, verticalAlign: 'middle',
          }}
        />
        {data.username}
        {character.isNPC && (
          <span style={{ fontSize: 11, color: '#3a5a3a', marginLeft: 6, fontStyle: 'italic' }}>
            (NPC)
          </span>
        )}
        {feminized && (
          <span
            data-testid="profile-feminized-badge"
            title="You have unlocked this NPC's female form."
            style={{
              marginLeft: 6, fontSize: 10, fontWeight: 'bold',
              color: '#fff', background: '#c14a7a',
              padding: '1px 6px', borderRadius: 3, letterSpacing: 1,
            }}
          >
            🌸 FEMININE FORM
          </span>
        )}
      </div>
      {(data && (data.ic_ooc || (character.isNPC ? 'IC' : null))) && (
        <div
          data-testid="profile-ic-ooc"
          style={{ textAlign: 'center', fontSize: 11, color: '#3a5a3a', fontWeight: 'bold', letterSpacing: 1, marginTop: 1 }}
        >
          [{(data.ic_ooc || (character.isNPC ? 'IC' : 'OOC')).toUpperCase()}]
        </div>
      )}
      {/* Msg #976 — Admin/Mod role badge */}
      {serverProfile && serverProfile.role && (
        <div
          data-testid="profile-role-badge"
          style={{
            textAlign: 'center', marginTop: 4,
          }}
        >
          <span
            style={{
              display: 'inline-block',
              padding: '2px 12px',
              fontSize: 11, fontWeight: 'bold', letterSpacing: 2,
              background: serverProfile.role === 'Administrator' ? '#7a1a1a' : '#33456a',
              color: '#f5f5f0',
              border: '1px solid rgba(0,0,0,0.4)',
              borderRadius: 12,
              textTransform: 'uppercase',
            }}
          >
            {serverProfile.role === 'Administrator' ? '👑 ' : '💎 '}{serverProfile.role}
          </span>
        </div>
      )}
      {serverProfile && serverProfile.updated_at && (
        <div
          data-testid="profile-updated-at"
          style={{ textAlign: 'center', fontSize: 10, color: '#3a5a3a', marginTop: 2, fontStyle: 'italic' }}
        >
          Last updated: {formatStamp(serverProfile.updated_at)}
        </div>
      )}

      {/* Tabs (Profile / Past Conversations) — for NPCs; and Edit tab for own profile */}
      {showTabStrip && (
        <div
          data-testid="profile-tabs"
          style={{
            display: 'flex',
            marginTop: 12,
            borderBottom: '1px solid #2d6b1e',
            paddingLeft: 4,
          }}
        >
          {showHistoryTab && (
            <>
              <span
                data-testid="profile-tab-profile"
                style={tabBtnStyle(tab === 'profile')}
                onClick={() => setTab('profile')}
              >
                PROFILE
              </span>
              <span
                data-testid="profile-tab-history"
                style={tabBtnStyle(tab === 'history')}
                onClick={() => setTab('history')}
              >
                PAST CONVERSATIONS
              </span>
              <span
                data-testid="profile-tab-past-rps"
                style={tabBtnStyle(tab === 'past_rps')}
                onClick={() => setTab('past_rps')}
              >
                PAST RPs
              </span>
              <span
                data-testid="profile-tab-stats"
                style={tabBtnStyle(tab === 'stats')}
                onClick={() => setTab('stats')}
              >
                CHARACTER STATS
              </span>
            </>
          )}
          {/* Msg #778 — Own-profile inline Edit tab */}
          {showOwnEditTab && (
            <span
              data-testid="profile-tab-edit"
              style={tabBtnStyle(false)}
              onClick={() => {
                onClose();
                if (onOpenEditProfile) onOpenEditProfile();
              }}
              title="Edit your own profile"
            >
              ✎ EDIT PROFILE
            </span>
          )}
          {/* Session AD (Msg #972) — Character stats tab for real users */}
          {showUserStatsTab && !showHistoryTab && (
            <>
              {/* Msg #992 — When we show the "Character Stats" tab for a
                  regular user, also render an explicit PROFILE tab so the
                  user can toggle back from Stats to Profile. */}
              <span
                data-testid="profile-tab-profile"
                style={tabBtnStyle(tab === 'profile')}
                onClick={() => setTab('profile')}
              >
                CHARACTER PROFILE
              </span>
              <span
                data-testid="profile-tab-user-stats"
                style={tabBtnStyle(tab === 'stats')}
                onClick={() => setTab('stats')}
              >
                CHARACTER STATS
              </span>
              {showInventoryTab && (
                <span
                  data-testid="profile-tab-inventory"
                  style={tabBtnStyle(tab === 'inventory')}
                  onClick={() => setTab('inventory')}
                >
                  INVENTORY
                </span>
              )}
            </>
          )}
          {/* Msg #991 — Admin-only: Edit + Delete user buttons on any profile. */}
          {isAdmin && !character.isNPC && character.name && character.name !== myName && (
            <>
              <span
                data-testid="profile-admin-edit-user"
                style={{ ...tabBtnStyle(false), background: '#4a3a1a', color: '#ffe1a0', border: '1px solid #a08a30' }}
                onClick={() => { if (onOpenEditProfile) onOpenEditProfile(character); }}
                title={`Admin: edit ${character.name}'s profile`}
              >
                ✎ ADMIN EDIT
              </span>
              <span
                data-testid="profile-admin-delete-user"
                style={{ ...tabBtnStyle(false), background: '#7a1f1f', color: '#fce2d0', border: '1px solid #3a0e0e' }}
                onClick={() => {
                  if (!onDeleteUser) return;
                  if (!window.confirm(`Delete user "${character.name}"? This will remove their account. Use HARD DELETE (checkbox in the popup) to also wipe chat, PMs, HP, and leaderboard.`)) return;
                  const hard = window.confirm('Hard delete? OK = also wipe chat/PMs/HP/leaderboard. Cancel = soft delete (account only).');
                  onDeleteUser(character.name, hard);
                  onClose();
                }}
                title={`Admin: delete ${character.name}`}
              >
                🗑 DELETE USER
              </span>
            </>
          )}
        </div>
      )}

      {/* ───── PROFILE TAB ───── */}
      {tab === 'profile' && (
        <>
          {/* Msg #798 — Profile video: centered at TOP of the profile tab
              (above the pictures). Supports mp4/webm (native <video>),
              YouTube + Vimeo (iframe embed), and .gif/.png/.jpg (still image). */}
          {data.video_url && (() => {
            const url = String(data.video_url).trim();
            const embed = _toEmbeddableVideoUrl(url);
            if (embed) {
              // Msg #973 — autoplay muted when the profile modal opens. The
              // iframe is unmounted when the modal closes, which stops playback.
              const autoplayEmbed = embed
                .replace(/autoplay=0/g, 'autoplay=1&mute=1')
                .replace(/(&|\?)autoplay=1(?!&mute)/g, '$1autoplay=1&mute=1');
              return (
                <div style={{ textAlign: 'center', marginTop: 6, marginBottom: 8 }}>
                  <iframe
                    data-testid="profile-video"
                    src={autoplayEmbed}
                    title="Character profile video"
                    allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
                    allowFullScreen
                    style={{
                      width: '100%', maxWidth: 480, height: 270,
                      border: '2px solid #2d6b1e', background: '#000',
                    }}
                  />
                </div>
              );
            }
            const looksLikeImg = /\.(gif|png|jpe?g|webp|avif)(\?|#|$)/i.test(url);
            if (looksLikeImg) {
              return (
                <div style={{ textAlign: 'center', marginTop: 6, marginBottom: 8 }}>
                  <img
                    data-testid="profile-video"
                    src={url}
                    alt="profile"
                    style={{ maxWidth: 360, maxHeight: 260, border: '2px solid #2d6b1e' }}
                    onError={(e) => (e.target.style.display = 'none')}
                  />
                </div>
              );
            }
            return (
              <div style={{ textAlign: 'center', marginTop: 6, marginBottom: 8 }}>
                <video
                  data-testid="profile-video"
                  src={url}
                  controls
                  playsInline
                  autoPlay
                  muted
                  preload="auto"
                  style={{ width: '100%', maxWidth: 480, maxHeight: 300, border: '2px solid #2d6b1e', background: '#000' }}
                />
              </div>
            );
          })()}

          {(() => {
            let gallery = Array.isArray(data.pictures) && data.pictures.length
              ? data.pictures.slice(0, 20)
              : (data.picture ? [data.picture] : []);
            // Msg #778 — Feminized monsters swap the "female" picture into slot 0
            // for THIS user only.  We use the last picture in the gallery as the
            // designated female variant (convention), or a dedicated
            // `feminine_picture` field if the NPC override exposes one.
            if (feminized && character && character.isNPC) {
              const femPic = data.feminine_picture
                || (gallery.length > 1 ? gallery[gallery.length - 1] : null);
              if (femPic) {
                gallery = [femPic, ...gallery.filter((p) => p !== femPic)];
              }
            }
            if (gallery.length === 0) {
              return (
                <div style={{ textAlign: 'center', marginTop: 12, fontStyle: 'italic', color: '#3a5a3a' }}>
                  (no portrait set)
                </div>
              );
            }
            // Msg #798 — Interactive gallery with ◀ ▶ arrows + counter.
            const total = gallery.length;
            const safeIdx = ((galleryIdx % total) + total) % total;
            const current = gallery[safeIdx];
            return (
              <div style={{ textAlign: 'center', marginTop: 12 }}>
                <div
                  data-testid="profile-picture-gallery"
                  style={{
                    position: 'relative',
                    display: 'inline-block',
                    maxWidth: 320,
                  }}
                >
                  <img
                    data-testid="profile-picture-main"
                    src={current}
                    alt={`${data.username} — ${safeIdx + 1}/${total}`}
                    style={{
                      display: 'block',
                      maxWidth: 320,
                      maxHeight: 380,
                      border: '2px solid #2d6b1e',
                    }}
                    onError={(e) => (e.target.style.display = 'none')}
                  />
                  {total > 1 && (
                    <>
                      <button
                        data-testid="profile-gallery-prev"
                        onClick={(e) => { e.stopPropagation(); setGalleryIdx((i) => (i - 1 + total) % total); }}
                        title="Previous"
                        style={{
                          position: 'absolute', top: '50%', left: 4,
                          transform: 'translateY(-50%)',
                          background: 'rgba(20,20,20,0.55)',
                          color: '#f5e6c0', border: '1px solid #c8a94d',
                          width: 34, height: 34, borderRadius: '50%',
                          cursor: 'pointer', fontSize: 18, lineHeight: '30px',
                          fontWeight: 'bold',
                        }}
                      >◀</button>
                      <button
                        data-testid="profile-gallery-next"
                        onClick={(e) => { e.stopPropagation(); setGalleryIdx((i) => (i + 1) % total); }}
                        title="Next"
                        style={{
                          position: 'absolute', top: '50%', right: 4,
                          transform: 'translateY(-50%)',
                          background: 'rgba(20,20,20,0.55)',
                          color: '#f5e6c0', border: '1px solid #c8a94d',
                          width: 34, height: 34, borderRadius: '50%',
                          cursor: 'pointer', fontSize: 18, lineHeight: '30px',
                          fontWeight: 'bold',
                        }}
                      >▶</button>
                    </>
                  )}
                </div>
                <div
                  data-testid="profile-gallery-count"
                  style={{
                    marginTop: 4, fontSize: 11, fontStyle: 'italic',
                    color: '#3a5a3a', letterSpacing: 1,
                  }}
                >
                  {safeIdx + 1} / {total} — {total} {total === 1 ? 'image' : 'images'}
                </div>
              </div>
            );
          })()}

          {/* Msg #798 — Dedicated animated-GIF slot (below pictures). */}
          {data.gif_url && (
            <div style={{ textAlign: 'center', marginTop: 8 }}>
              <img
                data-testid="profile-gif"
                src={String(data.gif_url).trim()}
                alt="profile gif"
                style={{ maxWidth: 320, maxHeight: 240, border: '2px solid #7a5aa8' }}
                onError={(e) => (e.target.style.display = 'none')}
              />
            </div>
          )}

          {/* Msg #799 + #800 — Auto-playing music embed with "Save to Playlist"
              action. React.memo prevents parent-poll re-renders from re-mounting
              the iframe (fixes the reported "auto-refresh" glitch). Modal close
              unmounts the whole block, which stops the audio. */}
          {data.music_html && (
            <div>
              <MusicEmbed musicHtml={data.music_html} />
              {character && character.isNPC && (
                <SoundtrackSaveButton npc={character.name} musicHtml={data.music_html} />
              )}
            </div>
          )}
          {/* Msg #993 — Adventurer Rank badge moved to Character Stats tab.
              (Removed from Profile tab to reduce clutter; the Stats tab now
              renders the rank + Atk/HP/MP/Def illustration.) */}

          <table style={{ margin: '14px auto 0', borderSpacing: '0 4px' }}>
            <tbody>
              {data.firstname && (
                <tr>
                  <td style={{ textAlign: 'right', paddingRight: 8 }}><b>Firstname :</b></td>
                  <td>{data.firstname}</td>
                </tr>
              )}
              {data.lastname && (
                <tr>
                  <td style={{ textAlign: 'right', paddingRight: 8 }}><b>Lastname :</b></td>
                  <td>{data.lastname}</td>
                </tr>
              )}
              {data.gender && (
                <tr>
                  <td style={{ textAlign: 'right', paddingRight: 8 }}><b>Gender :</b></td>
                  <td style={{ textTransform: 'capitalize' }}>{data.gender}</td>
                </tr>
              )}
              {data.website && (
                <tr>
                  <td style={{ textAlign: 'right', paddingRight: 8 }}><b>Website :</b></td>
                  <td>
                    <a href={data.website} target="_blank" rel="noreferrer" style={{ color: '#2d6b1e' }}>
                      {data.website}
                    </a>
                  </td>
                </tr>
              )}
              {data.showEmail && data.email && (
                <tr>
                  <td style={{ textAlign: 'right', paddingRight: 8 }}><b>E-mail :</b></td>
                  <td>{data.email}</td>
                </tr>
              )}
              {character.status && (
                <tr>
                  <td style={{ textAlign: 'right', paddingRight: 8 }}><b>Status :</b></td>
                  <td>{character.status}</td>
                </tr>
              )}
            </tbody>
          </table>

          <div style={{ marginTop: 14 }}>
            <div style={{ fontWeight: 'bold', marginBottom: 4 }}>Character Description:</div>
            <div
              style={{
                backgroundColor: '#f0fae6',
                border: '1px solid #2d6b1e',
                padding: 10,
                minHeight: 80,
                whiteSpace: 'pre-wrap',
                lineHeight: 1.5,
              }}
              dangerouslySetInnerHTML={{ __html: sanitizeProfileHTML(data.description) }}
            />
          </div>
        </>
      )}

      {/* ───── PAST CONVERSATIONS TAB ───── */}
      {tab === 'history' && showHistoryTab && (
        <div data-testid="profile-history-panel" style={{ marginTop: 12 }}>
          <div style={{ fontSize: 11, color: '#3a5a3a', marginBottom: 6, fontStyle: 'italic' }}>
            The last few private whispers between you and <b>{character.name.replace('_', ' ')}</b>.
          </div>
          {historyLoading && (
            <div style={{ padding: 20, textAlign: 'center', color: '#3a5a3a' }}>Loading…</div>
          )}
          {historyErr && !historyLoading && (
            <div data-testid="profile-history-error" style={{ color: '#a02020', textAlign: 'center', padding: 12 }}>
              Could not load conversation history: {historyErr}
            </div>
          )}
          {!historyLoading && !historyErr && history.length === 0 && (
            <div
              data-testid="profile-history-empty"
              style={{
                background: '#f0fae6', border: '1px solid #2d6b1e',
                padding: 20, textAlign: 'center', fontStyle: 'italic', color: '#3a5a3a',
              }}
            >
              You haven&apos;t shared any private moments with {character.name.replace('_', ' ')} yet. Whisper to her in the chatroom and the memory will begin here.
            </div>
          )}
          {!historyLoading && !historyErr && (favorites.length > 0) && (
            <div
              data-testid="profile-favorites-panel"
              style={{
                background: '#fff4e6', border: '1px solid #b07a2a',
                padding: '8px 10px', marginBottom: 10, fontSize: 12, lineHeight: 1.5,
              }}
            >
              <div style={{ fontWeight: 'bold', color: '#7a4a10', marginBottom: 4 }}>
                ♥ Pinned moments ({favorites.length})
              </div>
              {favorites.map((f) => (
                <div
                  key={f.id}
                  data-testid={`favorite-${f.id}`}
                  style={{
                    display: 'flex', alignItems: 'flex-start', gap: 6,
                    padding: '3px 0', borderBottom: '1px dotted #d9b07a',
                  }}
                >
                  <span style={{ flex: 1, color: '#3a2a10', fontStyle: 'italic', whiteSpace: 'pre-wrap' }}>
                    &ldquo;{f.text}&rdquo;
                  </span>
                  <span
                    data-testid={`favorite-remove-${f.id}`}
                    onClick={() => removeFavorite(f.id)}
                    title="Remove from favorites"
                    style={{ cursor: 'pointer', color: '#a02020', fontWeight: 'bold', fontSize: 13 }}
                  >
                    ✕
                  </span>
                </div>
              ))}
            </div>
          )}
          {!historyLoading && !historyErr && history.length > 0 && (
            <>
              <div
                data-testid="past-conv-scroll"
                style={{
                  background: '#f0fae6', border: '1px solid #2d6b1e',
                  maxHeight: 360, overflowY: 'auto',
                  overscrollBehavior: 'contain',
                  padding: '8px 10px',
                  fontSize: 12, lineHeight: 1.55,
                }}
              >
                {history.map((t, i) => (
                  <div
                    key={t.ts || i}
                    data-testid={`profile-history-turn-${i}`}
                    style={{
                      borderBottom: i < history.length - 1 ? '1px dotted #b6cf9c' : 'none',
                      padding: '6px 0',
                    }}
                  >
                    <div style={{ fontSize: 10, color: '#6a7a4a', marginBottom: 2 }}>
                      {formatStamp(t.ts)}
                    </div>
                    {t.user_text && (
                      <div style={{ marginBottom: 4 }}>
                        <b style={{ color: '#1a4a10' }}>You:</b>{' '}
                        <span style={{ whiteSpace: 'pre-wrap' }}>{t.user_text}</span>
                      </div>
                    )}
                    {t.npc_reply && (
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                        <span style={{ flex: 1 }}>
                          <b style={{ color: character.color || '#5a3a8a' }}>
                            {character.name.replace('_', ' ')}:
                          </b>{' '}
                          <span style={{ whiteSpace: 'pre-wrap' }}>{t.npc_reply}</span>
                        </span>
                        <span
                          data-testid={`remember-turn-${i}`}
                          onClick={() => addFavorite(t.npc_reply)}
                          title="♥ Remember this — pin this line to your favorites"
                          style={{
                            cursor: 'pointer', color: '#c02060', fontSize: 14,
                            paddingTop: 1, userSelect: 'none',
                          }}
                        >
                          ♥
                        </span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {onContinueScene && (
                <div style={{ textAlign: 'center', marginTop: 10 }}>
                  <button
                    data-testid="continue-scene-btn"
                    onClick={() => { onContinueScene(character); }}
                    style={{
                      background: '#1c3a1c',
                      color: '#ecfcd9',
                      border: '1px solid #2d6b1e',
                      padding: '5px 18px',
                      fontSize: 12,
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      letterSpacing: 1,
                    }}
                    title={`Open the Messenger and continue whispering with ${character.name.replace('_', ' ')}`}
                  >
                    ▶ CONTINUE THIS SCENE
                  </button>
                  <div style={{ fontSize: 10, color: '#3a5a3a', marginTop: 4, fontStyle: 'italic' }}>
                    Opens the Messenger and picks up where you left off.
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ───── PAST RPs TAB (NPC only — saved by clicking ▢ in chat) ───── */}
      {tab === 'past_rps' && showHistoryTab && (
        <div data-testid="profile-past-rps-panel" style={{ marginTop: 12 }}>
          <div style={{ fontSize: 11, color: '#3a5a3a', marginBottom: 6, fontStyle: 'italic' }}>
            Moments you saved with <b>{character.name.replace('_', ' ')}</b> by clicking the ▢ next to one of her chat lines.
          </div>
          {pastRpsLoading && (
            <div style={{ padding: 20, textAlign: 'center', color: '#3a5a3a' }}>Loading…</div>
          )}
          {!pastRpsLoading && pastRps.length === 0 && (
            <div
              data-testid="profile-past-rps-empty"
              style={{
                background: '#f0fae6', border: '1px solid #2d6b1e',
                padding: 20, textAlign: 'center', fontStyle: 'italic', color: '#3a5a3a',
              }}
            >
              No saved RP moments yet. Click the ▢ icon next to any of {character.name.replace('_', ' ')}&apos;s chat lines to remember it here.
            </div>
          )}
          {/* Msg #797 — Scrollable container with fixed maxHeight prevents the
              modal from resizing/glitching when RPs load; overscroll-contain
              stops the parent modal from scrolling when the user reaches an
              edge inside the list. */}
          {!pastRpsLoading && pastRps.length > 0 && (
            <div
              data-testid="past-rps-scroll"
              style={{
                maxHeight: 360, overflowY: 'auto',
                overscrollBehavior: 'contain',
                border: '1px solid #2d6b1e',
                background: '#f7fbe9',
                padding: 6,
              }}
            >
              {pastRps.map((rp) => (
                <div
                  key={rp.id}
                  data-testid={`past-rp-${rp.id}`}
                  style={{
                    background: '#f0fae6', border: '1px solid #2d6b1e',
                    padding: '8px 10px', marginBottom: 8, fontSize: 12,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 4 }}>
                    <div style={{ fontWeight: 'bold', color: '#1a4a10', flex: 1 }}>
                      {rp.title}
                    </div>
                    <span style={{ fontStyle: 'italic', color: '#3a5a3a', fontSize: 10 }}>
                      {formatStamp(rp.ts)}
                    </span>
                    {onContinueRp && (
                      <button
                        data-testid={`past-rp-continue-${rp.id}`}
                        onClick={() => onContinueRp(character, rp)}
                        title="Post the last saved line from this RP into the current chatroom"
                        style={{
                          background: '#1c3a1c', color: '#ecfcd9',
                          border: '1px solid #2d6b1e', padding: '2px 8px',
                          fontSize: 10, cursor: 'pointer', fontWeight: 'bold',
                          letterSpacing: 0.5,
                        }}
                      >
                        ▶ CONTINUE
                      </button>
                    )}
                    <button
                      data-testid={`past-rp-delete-${rp.id}`}
                      onClick={async () => {
                        if (!apiBase || !userId) return;
                        // eslint-disable-next-line no-alert
                        if (!window.confirm(`Delete this saved RP moment?`)) return;
                        try {
                          await fetch(`${apiBase}/npc/past_rps/${encodeURIComponent(rp.id)}?user_id=${encodeURIComponent(userId)}`, { method: 'DELETE' });
                          setPastRps((prev) => prev.filter((x) => x.id !== rp.id));
                        } catch { /* ignore */ }
                      }}
                      title="Delete this saved RP"
                      style={{
                        background: '#5a2a2a', color: '#f5e6c0',
                        border: '1px solid #3a1a1a', padding: '2px 8px',
                        fontSize: 10, cursor: 'pointer', fontWeight: 'bold',
                      }}
                    >
                      ✕
                    </button>
                  </div>
                  {(rp.messages || []).slice(0, 6).map((mm, i) => (
                    <div key={i} style={{ marginBottom: 2 }}>
                      <b style={{ color: mm.color || '#1a4a10' }}>{mm.user}:</b>{' '}
                      <span style={{ whiteSpace: 'pre-wrap' }}>{mm.text}</span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ───── CHARACTER STATS TAB ───── */}
      {tab === 'stats' && showHistoryTab && (
        <div data-testid="profile-stats-panel" style={{ marginTop: 12 }}>
          {!stats && (
            <div style={{ padding: 20, textAlign: 'center', color: '#3a5a3a' }}>Loading…</div>
          )}
          {stats && (
            <>
              <div style={{ fontSize: 11, color: '#3a5a3a', marginBottom: 10, fontStyle: 'italic' }}>
                {character.name.replace('_', ' ')}&apos;s feelings toward <b>you</b>.
                {!stats.has_hearts && ' This is a male monster — only dislike is tracked.'}
              </div>
              {stats.has_hearts && (
                <div data-testid="stat-affection" style={{ marginBottom: 14 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, fontWeight: 'bold', color: '#a02060', marginBottom: 4 }}>
                    <span>♥ Affection</span>
                    <span data-testid="stat-affection-value">{stats.affection} / {stats.max}</span>
                  </div>
                  <div style={{ display: 'flex', gap: 2 }}>
                    {Array.from({ length: stats.max }).map((_, i) => (
                      <span key={i} style={{ color: i < stats.affection ? '#d6336c' : '#d6cbb1', fontSize: 16, lineHeight: '16px' }}>♥</span>
                    ))}
                  </div>
                </div>
              )}
              <div data-testid="stat-dislike">
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, fontWeight: 'bold', color: '#5a2a2a', marginBottom: 4 }}>
                  <span>⚔ Dislike</span>
                  <span data-testid="stat-dislike-value">{stats.dislike} / {stats.max}</span>
                </div>
                <div style={{ display: 'flex', gap: 2 }}>
                  {Array.from({ length: stats.max }).map((_, i) => (
                    <span key={i} style={{ color: i < stats.dislike ? '#7a2828' : '#d6cbb1', fontSize: 16, lineHeight: '16px' }}>⚔</span>
                  ))}
                </div>
              </div>
              {/* ───── Health bar (max 15) ───── */}
              <div data-testid="stat-hp" style={{ marginTop: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, fontWeight: 'bold', color: '#1a5a10', marginBottom: 4 }}>
                  <span>❤ Health</span>
                  <span data-testid="stat-hp-value">{stats.hp} / {stats.hp_max}</span>
                </div>
                <div style={{
                  width: '100%', height: 14, border: '1px solid #1a4a10', background: '#d6e0c0', position: 'relative',
                }}>
                  <div style={{
                    width: `${(stats.hp / stats.hp_max) * 100}%`,
                    height: '100%',
                    background: stats.hp > stats.hp_max * 0.5 ? '#2d8a2d' : (stats.hp > stats.hp_max * 0.25 ? '#c9a13b' : '#a02020'),
                  }} />
                </div>
              </div>
              {/* ───── Defense (shield, 0-4) ───── */}
              <div data-testid="stat-defense" style={{ marginTop: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, fontWeight: 'bold', color: '#33456a', marginBottom: 4 }}>
                  <span>🛡 Defense</span>
                  <span data-testid="stat-defense-value">+{stats.defense}</span>
                </div>
                <div style={{ display: 'flex', gap: 2 }}>
                  {Array.from({ length: 4 }).map((_, i) => (
                    <span key={i} style={{ color: i < stats.defense ? '#33456a' : '#d6cbb1', fontSize: 18, lineHeight: '18px' }}>🛡</span>
                  ))}
                </div>
              </div>
              {/* ───── Combat action buttons (attack / heal via 1d12+def ≥ 5 rule) ───── */}
              {character.isNPC && userId && apiBase && (
                <div style={{ marginTop: 16, textAlign: 'center' }}>
                  <ActionBtn
                    data-testid="stat-attack-btn"
                    onClick={async () => {
                      try {
                        const r = await fetch(`${apiBase}/npc/combat/attack`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ user_id: userId, npc: character.name, action: 'attack', amount: 4 }),
                        });
                        const d = await r.json();
                        if (d) setStats((prev) => prev ? { ...prev, hp: d.hp_after } : prev);
                      } catch { /* ignore */ }
                    }}
                  >
                    ⚔ Attack (1d12)
                  </ActionBtn>
                  <ActionBtn
                    data-testid="stat-heal-btn"
                    onClick={async () => {
                      try {
                        const r = await fetch(`${apiBase}/npc/combat/attack`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ user_id: userId, npc: character.name, action: 'heal', amount: 4 }),
                        });
                        const d = await r.json();
                        if (d) setStats((prev) => prev ? { ...prev, hp: d.hp_after } : prev);
                      } catch { /* ignore */ }
                    }}
                  >
                    ✚ Heal
                  </ActionBtn>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Session AD (Msg #972) — Real-user CHARACTER STATS panel */}
      {tab === 'stats' && showUserStatsTab && !showHistoryTab && (
        <div data-testid="profile-user-stats-panel" style={{ marginTop: 12 }}>
          {!userLiveStats && (
            <div style={{ padding: 20, textAlign: 'center', opacity: 0.7 }}>Loading…</div>
          )}
          {userLiveStats && userLiveStats.rank && (
            <>
              <div style={{
                textAlign: 'center', marginBottom: 14, padding: '8px 12px',
                background: 'var(--room-bg, #fafaf7)',
                border: '1px solid var(--room-border, #c8c8b8)',
              }}>
                <div style={{ fontSize: 20 }}>
                  <span style={{ marginRight: 6 }}>{userLiveStats.rank.rank_icon}</span>
                  <b>{userLiveStats.rank.rank}</b>
                </div>
                <div style={{ fontSize: 11, opacity: 0.75, marginTop: 3 }}>
                  {userLiveStats.rank.kills || 0} monster kill{userLiveStats.rank.kills === 1 ? '' : 's'}
                  {userLiveStats.rank.next_rank
                    ? ` — ${userLiveStats.rank.to_next} more to ${userLiveStats.rank.next_rank}`
                    : ' — MAX RANK'}
                </div>
              </div>

              {userLiveStats.hp && (
                <div data-testid="user-stat-hp" style={{ marginTop: 6 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between',
                                fontSize: 12, fontWeight: 'bold', marginBottom: 4 }}>
                    <span>❤ Health</span>
                    <span data-testid="user-stat-hp-value">
                      {userLiveStats.hp.hp} / {userLiveStats.hp.hp_max}
                      {userLiveStats.hp.fainted ? '  💀 FAINTED' : ''}
                    </span>
                  </div>
                  <div style={{
                    width: '100%', height: 14,
                    border: '1px solid var(--room-border, #8a8a7a)',
                    background: 'var(--room-panel, #f0f0e8)',
                  }}>
                    <div style={{
                      width: `${userLiveStats.hp.hp_max ? (userLiveStats.hp.hp / userLiveStats.hp.hp_max) * 100 : 0}%`,
                      height: '100%',
                      background: userLiveStats.hp.fainted
                        ? '#a02020'
                        : (userLiveStats.hp.hp > userLiveStats.hp.hp_max * 0.5
                            ? '#2d8a2d'
                            : (userLiveStats.hp.hp > userLiveStats.hp.hp_max * 0.25 ? '#c9a13b' : '#a02020')),
                      transition: 'width 400ms ease-in-out',
                    }} />
                  </div>
                </div>
              )}

              {/* Msg #979 — Magic Points bar */}
              {userLiveStats.hp && userLiveStats.hp.mp_max !== undefined && (
                <div data-testid="user-stat-mp" style={{ marginTop: 6 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between',
                                fontSize: 12, fontWeight: 'bold', marginBottom: 4 }}>
                    <span>🔮 Magic (MP)</span>
                    <span data-testid="user-stat-mp-value">
                      {userLiveStats.hp.mp} / {userLiveStats.hp.mp_max}
                    </span>
                  </div>
                  <div style={{
                    width: '100%', height: 14,
                    border: '1px solid var(--room-border, #8a8a7a)',
                    background: 'var(--room-panel, #f0f0e8)',
                  }}>
                    <div style={{
                      width: `${userLiveStats.hp.mp_max ? (userLiveStats.hp.mp / userLiveStats.hp.mp_max) * 100 : 0}%`,
                      height: '100%',
                      background: '#5a3a8a',
                      transition: 'width 400ms ease-in-out',
                    }} />
                  </div>
                </div>
              )}

              <div data-testid="user-stat-defense" style={{ marginTop: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between',
                              fontSize: 12, fontWeight: 'bold', marginBottom: 4 }}>
                  <span>🛡 Defense</span>
                  <span data-testid="user-stat-defense-value">+{userLiveStats.rank.defense ?? 1}</span>
                </div>
                <div style={{ display: 'flex', gap: 2 }}>
                  {Array.from({ length: 4 }).map((_, i) => (
                    <span key={i} style={{
                      color: i < (userLiveStats.rank.defense || 0) ? '#33456a' : '#c8c8b8',
                      fontSize: 18, lineHeight: '18px',
                    }}>🛡</span>
                  ))}
                </div>
              </div>

              <div data-testid="user-stat-attack" style={{ marginTop: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between',
                              fontSize: 12, fontWeight: 'bold', marginBottom: 4 }}>
                  <span>⚔ Attack</span>
                  <span data-testid="user-stat-attack-value">+{userLiveStats.rank.attack ?? 1}</span>
                </div>
                <div style={{ display: 'flex', gap: 2 }}>
                  {Array.from({ length: 5 }).map((_, i) => (
                    <span key={i} style={{
                      color: i < (userLiveStats.rank.attack || 0) ? '#7a1f1f' : '#c8c8b8',
                      fontSize: 18, lineHeight: '18px',
                    }}>⚔</span>
                  ))}
                </div>
              </div>

              <div style={{ marginTop: 14, fontSize: 11, opacity: 0.7, textAlign: 'center' }}>
                Damage dealt: <b>{userLiveStats.rank.damage_dealt || 0}</b> HP
              </div>
            </>
          )}
          {userLiveStats && !userLiveStats.rank && (
            <div style={{ padding: 20, textAlign: 'center', opacity: 0.7 }}>
              No combat record yet — go swing at a monster.
            </div>
          )}
        </div>
      )}

      {/* Msg #996 — INVENTORY tab body (own profile only). */}
      {tab === 'inventory' && showInventoryTab && (
        <InventoryTabBody
          apiBase={apiBase}
          username={myName}
        />
      )}


      <div style={{ textAlign: 'center', marginTop: 14 }}>
        {onPm && character.name && (character.isNPC || !myName || String(character.name).toLowerCase() !== String(myName).toLowerCase()) && (
          <ActionBtn
            data-testid="profile-pm-btn"
            onClick={() => onPm(character)}
          >
            ✉ Message
          </ActionBtn>
        )}
        {onSendFriendRequest && character.name && (!myName || String(character.name).toLowerCase() !== String(myName).toLowerCase()) && (
          <ActionBtn
            data-testid="profile-friend-request-btn"
            onClick={() => onSendFriendRequest(character)}
          >
            🤝 Friend
          </ActionBtn>
        )}
        {/* Msg #892 — cloak toggle visible ONLY to admins viewing their OWN profile */}
        {isAdmin && myName && String(character.name).toLowerCase() === String(myName).toLowerCase() && onToggleCloak && (
          <ActionBtn
            data-testid="profile-cloak-btn"
            onClick={() => onToggleCloak(!cloaked)}
            style={{
              backgroundColor: cloaked ? '#5d2a2a' : '#1c3a3a',
              borderColor: cloaked ? '#3a1a1a' : '#0f2020',
            }}
          >
            {cloaked ? '👁‍🗨 Uncloak' : '🥷 Cloak'}
          </ActionBtn>
        )}
        {isAdmin && character.isNPC && onEditNpc && (
          <ActionBtn
            data-testid="admin-edit-npc-btn"
            onClick={() => onEditNpc(character)}
          >
            Edit NPC (Admin)
          </ActionBtn>
        )}
        {/* Msg #999 — Admin: delete custom / dynamic NPC directly from profile. */}
        {isAdmin && character.isNPC && onDeleteNpc && (
          <ActionBtn
            data-testid="admin-delete-npc-btn"
            onClick={() => {
              if (!window.confirm(`Delete NPC "${character.name}"? Only admin-created / dynamic NPCs will be removed; built-in NPCs are ignored.`)) return;
              onDeleteNpc(character.name);
              onClose();
            }}
            style={{ backgroundColor: '#7a1f1f', borderColor: '#3a0e0e', color: '#fce2d0' }}
          >
            🗑 Delete NPC
          </ActionBtn>
        )}
        {/* Msg #774 — Admin NPC Takeover / Puppet toggle */}
        {isAdmin && character.isNPC && onTogglePuppet && (
          <ActionBtn
            data-testid="admin-puppet-npc-btn"
            onClick={() => onTogglePuppet(
              (puppetNpc && puppetNpc.name === character.name) ? null : character,
            )}
            style={{
              backgroundColor: (puppetNpc && puppetNpc.name === character.name) ? '#5d2a2a' : '#3a1a5a',
              borderColor:     (puppetNpc && puppetNpc.name === character.name) ? '#3a1a1a' : '#1a0a2a',
            }}
          >
            {(puppetNpc && puppetNpc.name === character.name)
              ? '🎭 Release Puppet'
              : '🎭 Puppet This NPC'}
          </ActionBtn>
        )}
        <ActionBtn onClick={onClose}>Close</ActionBtn>
      </div>
    </ModalShell>
  );
};


// ───── Admin-only modal to edit an NPC's description / picture / color / persona ─────
export const EditNpcModal = ({ open, onClose, character, requester, apiBase }) => {
  const [description, setDescription] = React.useState('');
  const [picture, setPicture] = React.useState('');
  // Msg #778 — Admin can attach up to 20 additional gallery images per NPC.
  const [pictures, setPictures] = React.useState([]);
  const [femininePicture, setFemininePicture] = React.useState('');
  // Msg #797 batch — Website link + embedded video URL for NPC profiles.
  const [website, setWebsite] = React.useState('');
  const [videoUrl, setVideoUrl] = React.useState('');
  // Msg #798 — Dedicated animated GIF slot + free-form embed HTML for music
  const [gifUrl, setGifUrl] = React.useState('');
  const [musicHtml, setMusicHtml] = React.useState('');
  const [color, setColor] = React.useState('');
  const [persona, setPersona] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [err, setErr] = React.useState('');

  // Msg #796 — Only re-initialize when the modal opens or the target NPC
  // *identity* changes.  Previously depending on `character` (a new object
  // returned by applyOverride() on every parent render) caused this effect to
  // re-fire on every keystroke, wiping the picture-slot text mid-typing.
  const charName = character ? character.name : null;
  React.useEffect(() => {
    if (!open || !character) return;
    setDescription(character.description || '');
    setPicture(character.picture || '');
    setPictures(Array.isArray(character.pictures) ? character.pictures.slice(0, 20) : []);
    setFemininePicture(character.feminine_picture || '');
    setWebsite(character.website || '');
    setVideoUrl(character.video_url || '');
    setGifUrl(character.gif_url || '');
    setMusicHtml(character.music_html || '');
    setColor(character.color || '');
    setPersona('');
    setErr('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, charName]);

  if (!open || !character) return null;

  const handleSave = async () => {
    setSaving(true);
    setErr('');
    try {
      const cleanedPics = (pictures || [])
        .map((s) => (s || '').trim())
        .filter(Boolean)
        .slice(0, 20);
      const body = {
        requester,
        npc: character.name,
        description: description || null,
        picture: picture || null,
        pictures: cleanedPics,
        feminine_picture: femininePicture || null,
        website: (website || '').trim() || null,
        video_url: (videoUrl || '').trim() || null,
        gif_url: (gifUrl || '').trim() || null,
        music_html: (musicHtml || '').trim() || null,
        color: color || null,
      };
      if (persona && persona.trim().length > 0) body.persona = persona;
      const res = await fetch(`${apiBase}/admin/edit_npc`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || `HTTP ${res.status}`);
      }
      onClose({
        description, picture, pictures: cleanedPics,
        feminine_picture: femininePicture,
        website: (website || '').trim(),
        video_url: (videoUrl || '').trim(),
        gif_url: (gifUrl || '').trim(),
        music_html: (musicHtml || '').trim(),
        color,
      });
    } catch (e) {
      setErr(String(e.message || e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell open={open} onClose={() => onClose(null)} width={640}>
      <div style={{ textAlign: 'center', fontWeight: 'bold', fontSize: 20 }}>
        Edit NPC Profile (Admin)
      </div>
      <div
        style={{
          textAlign: 'center', fontWeight: 'bold', fontSize: 16,
          marginTop: 4, color: character.color || '#1c3f1c',
        }}
      >
        {character.name}
      </div>
      <table style={{ margin: '14px auto 0', borderSpacing: '0 6px', width: '100%' }}>
        <tbody>
          <tr>
            <td style={{ textAlign: 'right', paddingRight: 8, verticalAlign: 'top', whiteSpace: 'nowrap' }}>
              <b>Picture URL :</b>
            </td>
            <td>
              <input
                data-testid="edit-npc-picture"
                value={picture}
                onChange={(e) => setPicture(e.target.value)}
                style={{ width: '100%', border: '1px solid #2d6b1e', padding: '3px 6px' }}
              />
            </td>
          </tr>
          <tr>
            <td style={{ textAlign: 'right', paddingRight: 8, verticalAlign: 'top', whiteSpace: 'nowrap' }}>
              <b>Extra gallery :</b>
              <br />
              <span style={{ fontSize: 10, color: '#4a5a3a', fontWeight: 'normal' }}>(up to 20)</span>
            </td>
            <td>
              {Array.from({ length: 20 }).map((_, i) => (
                <div key={i} style={{ display: 'flex', gap: 4, marginBottom: 3 }}>
                  <input
                    data-testid={`edit-npc-picture-slot-${i}`}
                    value={pictures[i] || ''}
                    onChange={(e) => {
                      const next = [...pictures];
                      next[i] = e.target.value;
                      setPictures(next);
                    }}
                    placeholder={`slot ${i + 1}`}
                    style={{ width: '100%', border: '1px solid #2d6b1e', padding: '2px 6px', fontSize: 11 }}
                  />
                </div>
              ))}
            </td>
          </tr>
          <tr>
            <td style={{ textAlign: 'right', paddingRight: 8, verticalAlign: 'top', whiteSpace: 'nowrap' }}>
              <b>Feminine picture :</b>
              <br />
              <span style={{ fontSize: 10, color: '#4a5a3a', fontWeight: 'normal' }}>
                (shown as main<br />when player unlocks<br />feminine form)
              </span>
            </td>
            <td>
              <input
                data-testid="edit-npc-feminine-picture"
                value={femininePicture}
                onChange={(e) => setFemininePicture(e.target.value)}
                placeholder="https://... (used when feminized)"
                style={{ width: '100%', border: '1px solid #c14a7a', padding: '3px 6px' }}
              />
            </td>
          </tr>
          <tr>
            <td style={{ textAlign: 'right', paddingRight: 8, verticalAlign: 'top', whiteSpace: 'nowrap' }}>
              <b>Website :</b>
              <br />
              <span style={{ fontSize: 10, color: '#4a5a3a', fontWeight: 'normal' }}>(optional external link)</span>
            </td>
            <td>
              <input
                data-testid="edit-npc-website"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                placeholder="https://example.com"
                style={{ width: '100%', border: '1px solid #2d6b1e', padding: '3px 6px' }}
              />
            </td>
          </tr>
          <tr>
            <td style={{ textAlign: 'right', paddingRight: 8, verticalAlign: 'top', whiteSpace: 'nowrap' }}>
              <b>Video URL :</b>
              <br />
              <span style={{ fontSize: 10, color: '#4a5a3a', fontWeight: 'normal' }}>
                (mp4 / webm / gif —<br />plays inline on profile<br />+ YouTube + Vimeo)
              </span>
            </td>
            <td>
              <input
                data-testid="edit-npc-video"
                value={videoUrl}
                onChange={(e) => setVideoUrl(e.target.value)}
                placeholder="https://.../scene.mp4  |  youtu.be/xxx  |  vimeo.com/123"
                style={{ width: '100%', border: '1px solid #2d6b1e', padding: '3px 6px' }}
              />
            </td>
          </tr>
          <tr>
            <td style={{ textAlign: 'right', paddingRight: 8, verticalAlign: 'top', whiteSpace: 'nowrap' }}>
              <b>GIF URL :</b>
              <br />
              <span style={{ fontSize: 10, color: '#4a5a3a', fontWeight: 'normal' }}>(dedicated animated slot)</span>
            </td>
            <td>
              <input
                data-testid="edit-npc-gif"
                value={gifUrl}
                onChange={(e) => setGifUrl(e.target.value)}
                placeholder="https://.../loop.gif"
                style={{ width: '100%', border: '1px solid #7a5aa8', padding: '3px 6px' }}
              />
            </td>
          </tr>
          <tr>
            <td style={{ textAlign: 'right', paddingRight: 8, verticalAlign: 'top', whiteSpace: 'nowrap' }}>
              <b>Music embed :</b>
              <br />
              <span style={{ fontSize: 10, color: '#4a5a3a', fontWeight: 'normal' }}>
                (paste raw HTML iframe<br />from Spotify/SoundCloud/<br />Bandcamp/YT — plays while<br />profile is open)
              </span>
            </td>
            <td>
              <textarea
                data-testid="edit-npc-music"
                value={musicHtml}
                onChange={(e) => setMusicHtml(e.target.value)}
                rows={3}
                placeholder='<iframe src="https://open.spotify.com/embed/track/..." width="300" height="80" frameborder="0" allow="autoplay"></iframe>'
                style={{ width: '100%', border: '1px solid #5a4a1d', padding: '3px 6px', fontFamily: 'monospace', fontSize: 11 }}
              />
            </td>
          </tr>
          <tr>
            <td style={{ textAlign: 'right', paddingRight: 8, verticalAlign: 'top', whiteSpace: 'nowrap' }}>
              <b>Color :</b>
            </td>
            <td>
              <input
                data-testid="edit-npc-color"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                placeholder="#6a3a8a"
                style={{ width: 140, border: '1px solid #2d6b1e', padding: '3px 6px' }}
              />
              <span style={{
                display: 'inline-block', marginLeft: 8, width: 18, height: 18,
                background: color || '#888', border: '1px solid #2d6b1e',
                verticalAlign: 'middle',
              }} />
            </td>
          </tr>
          <tr>
            <td style={{ textAlign: 'right', paddingRight: 8, verticalAlign: 'top', whiteSpace: 'nowrap' }}>
              <b>Description :</b>
            </td>
            <td>
              <textarea
                data-testid="edit-npc-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={6}
                style={{ width: '100%', border: '1px solid #2d6b1e', padding: '3px 6px', fontFamily: 'inherit' }}
              />
              <div style={{ fontSize: 10, color: '#3a5a3a', marginTop: 2 }}>
                HTML allowed: &lt;b&gt;, &lt;i&gt;, &lt;u&gt;, &lt;br&gt;, &lt;p&gt;, &lt;center&gt;, &lt;img&gt;.
              </div>
            </td>
          </tr>
          <tr>
            <td style={{ textAlign: 'right', paddingRight: 8, verticalAlign: 'top', whiteSpace: 'nowrap' }}>
              <b>Persona (advanced):</b>
            </td>
            <td>
              <textarea
                data-testid="edit-npc-persona"
                value={persona}
                onChange={(e) => setPersona(e.target.value)}
                rows={5}
                placeholder="(leave blank to keep current LLM persona unchanged)"
                style={{ width: '100%', border: '1px solid #2d6b1e', padding: '3px 6px', fontFamily: 'inherit' }}
              />
              <div style={{ fontSize: 10, color: '#3a5a3a', marginTop: 2 }}>
                System prompt the LLM will receive. Leave blank to keep the default.
              </div>
            </td>
          </tr>
        </tbody>
      </table>
      {err && (
        <div data-testid="edit-npc-error" style={{ color: '#a02020', textAlign: 'center', marginTop: 8 }}>
          {err}
        </div>
      )}
      <div style={{ textAlign: 'center', marginTop: 14 }}>
        <ActionBtn data-testid="edit-npc-save" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </ActionBtn>
        <ActionBtn onClick={() => onClose(null)}>Cancel</ActionBtn>
      </div>
    </ModalShell>
  );
};
